import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, TrendingUp, ShieldCheck, Zap } from 'lucide-react'
import { TremorLogo } from '@/shared/components/ui/TremorLogo'
import { cn } from '@/shared/lib/utils'
import { useAuthStore } from '@/core/stores/auth'
import api from '@/shared/lib/api'

const FEATURES = [
  { icon: TrendingUp,   text: 'Intelligence marché SEA en temps réel' },
  { icon: ShieldCheck,  text: 'Gestion e-réputation automatisée' },
  { icon: Zap,          text: 'Recommandations IA actionnables' },
]

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  )
}

export function LoginPage() {
  const navigate = useNavigate()
  const { login, isAuthenticated } = useAuthStore()

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')
  const [showPwd,  setShowPwd]  = useState(false)
  const [loading,  setLoading]  = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [error,    setError]    = useState<string | null>(null)

  useEffect(() => { if (isAuthenticated) navigate('/home', { replace: true }) }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const { data } = await api.post('/auth/email-login', { email, password })
      await login(data.access_token)
      navigate('/home', { replace: true })
    } catch (err: any) {
      const detail = err?.response?.data?.detail
      setError(detail || 'Email ou mot de passe incorrect.')
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleLogin = async () => {
    setGoogleLoading(true)
    setError(null)
    try {
      const { data } = await api.get('/auth/google')
      window.location.href = data.auth_url
    } catch {
      setError('Impossible de se connecter avec Google.')
      setGoogleLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left — Onyx branding */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-between p-12 relative overflow-hidden" style={{ background: '#1A1816' }}>
        <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(201,168,76,0.18)' }} />
        <div className="absolute bottom-[-15%] right-[-10%] w-[50%] h-[50%] rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(232,86,42,0.12)' }} />
        <div className="absolute top-[40%] right-[5%] w-[30%] h-[30%] rounded-full blur-[100px] pointer-events-none" style={{ background: 'rgba(15,113,115,0.10)' }} />

        <div className="relative z-10 flex items-center gap-3">
          <TremorLogo size={32} className="text-gold" />
          <p className="font-serif text-2xl font-bold text-paper tracking-tight">
            tremor<em className="not-italic font-light text-gold">app</em>
          </p>
        </div>

        <div className="relative z-10">
          <h2 className="font-serif text-4xl font-bold leading-tight mb-4" style={{ color: '#F5F3EF', letterSpacing: '-0.03em' }}>
            L'intelligence marché pour les{' '}
            <em className="not-italic" style={{ color: '#C9A84C' }}>agences</em>{' '}
            qui gagnent.
          </h2>
          <p className="font-sans text-base font-light mb-8 leading-relaxed" style={{ color: 'rgba(245,243,239,0.55)' }}>
            Capturez la demande, simulez vos budgets, optimisez votre présence locale.
          </p>
          <ul className="space-y-3">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3">
                <Icon size={16} style={{ color: 'rgba(201,168,76,0.75)', flexShrink: 0 }} />
                <span className="font-sans text-sm font-light" style={{ color: 'rgba(245,243,239,0.65)' }}>{text}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="relative z-10 text-[9px] font-sans font-medium tracking-[0.18em] uppercase" style={{ color: 'rgba(201,168,76,0.4)' }}>
          Version 1.0 · {new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
        </p>
      </div>

      {/* Right — Login form */}
      <div className="flex-1 flex flex-col justify-center px-8 py-12 sm:px-12 bg-paper border-l border-ink-border/20">
        <div className="lg:hidden mb-8 flex items-center gap-2.5">
          <TremorLogo size={24} className="text-gold" />
          <p className="font-serif text-xl font-bold text-ink tracking-tight">
            tremor<em className="not-italic font-light text-gold">app</em>
          </p>
        </div>

        <div className="w-full max-w-sm mx-auto">
          <h1 className="font-serif text-2xl font-bold text-ink tracking-tight mb-1">Connexion</h1>
          <p className="font-sans text-sm font-light text-ink-muted mb-8">Bienvenue sur TremorApp</p>

          {/* Google OAuth */}
          <button onClick={handleGoogleLogin} disabled={googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-2.5 rounded-lg border border-ink-border bg-white text-sm font-medium text-ink hover:bg-paper-warm transition-all disabled:opacity-50 mb-6">
            {googleLoading ? (
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
            ) : (
              <GoogleIcon />
            )}
            Continuer avec Google
          </button>

          {/* Separator */}
          <div className="flex items-center gap-3 mb-6">
            <div className="flex-1 h-px bg-ink-border" />
            <span className="text-[11px] text-ink-faint uppercase tracking-wider">ou</span>
            <div className="flex-1 h-px bg-ink-border" />
          </div>

          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <div>
              <label htmlFor="email" className="block text-label-caps text-ink-muted uppercase tracking-wider mb-1.5">Adresse email</label>
              <input id="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)}
                placeholder="vous@agence.fr"
                className="w-full bg-white border border-ink-border rounded-lg px-4 py-2.5 font-sans text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label htmlFor="password" className="block text-label-caps text-ink-muted uppercase tracking-wider">Mot de passe</label>
                <Link to="/forgot-password" className="text-xs text-gold hover:text-gold-dark underline underline-offset-2 transition-colors">Mot de passe oublié ?</Link>
              </div>
              <div className="relative">
                <input id="password" type={showPwd ? 'text' : 'password'} autoComplete="current-password" required value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white border border-ink-border rounded-lg px-4 py-2.5 pr-11 font-sans text-sm text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold transition-colors" />
                <button type="button" onClick={() => setShowPwd(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-faint hover:text-ink-muted transition-colors">
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {error && <p className="text-sm text-brick font-sans font-medium" role="alert">{error}</p>}

            <button type="submit" disabled={loading || !email || !password}
              className="w-full bg-brick hover:bg-brick-hover text-white font-sans text-sm font-medium rounded-lg px-4 py-2.5 transition-all duration-150 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              {loading ? (
                <><svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Connexion…</>
              ) : 'Se connecter'}
            </button>
          </form>

          <p className="mt-6 text-sm text-ink-muted font-light text-center">
            Pas encore de compte ?{' '}
            <Link to="/signup" className="text-brick font-medium hover:underline underline-offset-2">Créer un espace</Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
